/*
 * Roles, what each may do, and who holds them (System Administration).
 *
 * Shapes follow RoleController, RolePermissionController and
 * RoleAssignmentController. Changes to rights or holders are staged: proposing
 * creates a pending change, and nothing about the role moves until approval.
 */
import { users } from "@/mock/admin";
import { staff } from "@/mock/masters";
import { ok, created, fail, notFound, invalid, paginate, matches, nextId, daysAgo, hoursAgo, now } from "./util";

const conflict = (message, details = null) => fail(message, { code: "RECORD_CONFLICT", ...(details ? { details } : {}) }, 409);

/* ------------------------------------------------------------------ */
/* Roles                                                                 */
/* ------------------------------------------------------------------ */

// Labels match crmRoles in mock/admin.js, which is how the sample users name them.
export const roles = [
  { id: 1, name: "admin", label: "CTO/Admin", description: "Administers the system: users, roles, configuration and integrations. Always holds every right.", is_system: true, sort_order: 1 },
  { id: 2, name: "sales", label: "Sales", description: "Works their own prospects, leads, opportunities and quotations.", is_system: true, sort_order: 2 },
  { id: 3, name: "sales-manager", label: "Sales Manager", description: "Sees the team's pipeline, assigns leads and approves quotation discounts within limits.", is_system: true, sort_order: 3 },
  { id: 4, name: "customer-success", label: "Customer Success", description: "Owns customer health, reviews, renewals and churn risk for their accounts.", is_system: true, sort_order: 4 },
  { id: 5, name: "implementation", label: "Implementation", description: "Runs onboarding projects from kick-off to sign-off.", is_system: true, sort_order: 5 },
  { id: 6, name: "support", label: "Support", description: "Works the ticket queue and sees the API clients that raise tickets.", is_system: true, sort_order: 6 },
  { id: 7, name: "marketing", label: "Marketing", description: "Builds prospect lists, imports campaign leads and reads marketing reports.", is_system: true, sort_order: 7 },
  { id: 8, name: "finance", label: "Finance", description: "Approves contracts, renewal pricing and exits; reads revenue reports.", is_system: true, sort_order: 8 },
  { id: 9, name: "management", label: "Management", description: "Sees everything across the company and signs off approvals that reach them.", is_system: true, sort_order: 9 },
  { id: 10, name: "audit-risk", label: "Audit/Risk", description: "Read-only across the company, including the activity log.", is_system: true, sort_order: 10 },
  { id: 11, name: "partner-manager", label: "Channel Partner Manager", description: "For the reseller programme planned for next year.", is_system: false, sort_order: 20, is_active: false },
].map((r) => ({ is_active: true, created_at: daysAgo(400 - r.id), ...r }));

const roleById = (id) => roles.find((r) => String(r.id) === String(id));
const roleByLabel = (label) => roles.find((r) => r.label === label);

/* ------------------------------------------------------------------ */
/* Permissions, filed by module and group (config/permission-modules)    */
/* ------------------------------------------------------------------ */

const CATALOGUE = [
  ["Prospects", "Prospects", "prospects", ["view", "view-team", "view-all", "create", "update", "assign", "convert", "export", "delete"]],
  ["Leads & Opportunities", "Leads", "leads", ["view", "view-team", "create", "update", "assign", "qualify", "delete"]],
  ["Leads & Opportunities", "Opportunities", "opportunities", ["view", "view-team", "create", "update", "close-won", "close-lost", "delete"]],
  ["Quotations", "Quotations", "quotations", ["view", "create", "update", "send", "approve-discount", "delete"]],
  ["Customers", "Customers", "customers", ["view", "create", "update", "merge", "export"]],
  ["Customers", "Contracts", "contracts", ["view", "create", "update", "approve", "terminate"]],
  ["Onboarding", "Onboarding", "onboarding", ["view", "create", "update", "sign-off"]],
  ["Customer Success", "Health & Reviews", "success", ["view", "update", "log-review"]],
  ["Support", "Tickets", "tickets", ["view", "view-all", "create", "update", "assign", "close", "reopen"]],
  ["Support", "Ticket API Clients", "ticket-clients", ["view", "create", "update", "rotate-secret", "suspend"]],
  ["Renewals & Retention", "Renewals", "renewals", ["view", "update", "approve"]],
  ["Renewals & Retention", "Churn", "churn", ["view", "update"]],
  ["Renewals & Retention", "Exits", "exits", ["view", "create", "approve"]],
  ["Reporting", "Reports", "reports", ["view", "export", "schedule"]],
  ["Reporting", "KPIs", "kpis", ["view", "configure"]],
  ["Reporting", "Dashboards", "dashboards", ["executive"]],
  ["Reporting", "Exports", "exports", ["view", "create"]],
  ["Reporting", "Imports", "imports", ["view", "create"]],
  ["Documents", "Documents", "documents", ["view", "upload", "delete"]],
  ["System Administration", "Users", "users", ["view", "create", "update", "deactivate"]],
  ["System Administration", "Roles & Permissions", "roles", ["view", "create", "update", "delete"]],
  ["System Administration", "Roles & Permissions", "role-permissions", ["view", "propose"]],
  ["System Administration", "Roles & Permissions", "role-assignments", ["view", "propose"]],
  ["System Administration", "Approvals", "approvals", ["view", "act"]],
  ["System Administration", "Approvals", "approval-workflows", ["view", "manage"]],
  ["System Administration", "Settings", "settings", ["view", "update"]],
  ["System Administration", "Audit", "logs", ["view"]],
];

const VERBS = { view: "View", "view-team": "View the team's", "view-all": "View all", create: "Create", update: "Edit", delete: "Delete", assign: "Assign", export: "Export", approve: "Approve", upload: "Upload" };

const permissionLabel = (group, action) => {
  const noun = group.toLowerCase();
  return VERBS[action] ? `${VERBS[action]} ${noun}` : `${action.charAt(0).toUpperCase()}${action.slice(1).replace(/-/g, " ")} — ${noun}`;
};

export const permissions = CATALOGUE.flatMap(([module, group, prefix, actions]) =>
  actions.map((action) => ({ name: `${prefix}.${action}`, label: permissionLabel(group, action), module, group }))
).map((p, i) => ({ id: i + 1, ...p }));

const permissionByName = (name) => permissions.find((p) => p.name === name);
const permissionById = (id) => permissions.find((p) => String(p.id) === String(id));

// What each role holds: "prefix.action" names, "prefix.*" for every action, "*.view" for every view.
const GRANTS = {
  admin: ["*"],
  sales: ["prospects.view", "prospects.create", "prospects.update", "prospects.convert", "leads.view", "leads.create", "leads.update", "leads.qualify", "opportunities.view", "opportunities.create", "opportunities.update", "opportunities.close-won", "opportunities.close-lost", "quotations.view", "quotations.create", "quotations.update", "quotations.send", "customers.view", "documents.view", "documents.upload", "reports.view"],
  "sales-manager": ["prospects.*", "leads.*", "opportunities.*", "quotations.*", "customers.view", "contracts.view", "documents.view", "documents.upload", "reports.view", "reports.export", "exports.*", "approvals.*", "kpis.view"],
  "customer-success": ["customers.view", "customers.update", "contracts.view", "success.*", "renewals.view", "renewals.update", "churn.*", "tickets.view", "documents.view", "reports.view"],
  implementation: ["onboarding.*", "customers.view", "contracts.view", "documents.view", "documents.upload", "tickets.view"],
  support: ["tickets.view", "tickets.create", "tickets.update", "tickets.assign", "tickets.close", "ticket-clients.view", "customers.view"],
  marketing: ["prospects.view", "prospects.view-all", "prospects.create", "prospects.update", "prospects.export", "reports.view", "imports.*", "exports.*"],
  finance: ["contracts.view", "contracts.approve", "quotations.view", "quotations.approve-discount", "renewals.view", "renewals.approve", "exits.view", "exits.approve", "customers.view", "reports.*", "kpis.view", "exports.*", "approvals.*"],
  management: ["*.view", "*.view-all", "*.view-team", "reports.*", "kpis.*", "dashboards.executive", "approvals.*", "exits.approve"],
  "audit-risk": ["*.view", "*.view-all", "logs.view"],
  "partner-manager": [],
};

const grantMatches = (grant, name) => {
  if (grant === "*") return true;
  const [prefix, action] = grant.split(".");
  const [p, a] = name.split(/\.(.+)/);
  return (prefix === "*" || prefix === p) && (action === "*" || action === a);
};

// role id → Set of permission ids it holds.
const held = new Map(
  roles.map((r) => [r.id, new Set(permissions.filter((p) => (GRANTS[r.name] ?? []).some((g) => grantMatches(g, p.name))).map((p) => p.id))])
);

/* ------------------------------------------------------------------ */
/* People and the roles they hold                                        */
/* ------------------------------------------------------------------ */

const staffOf = (u) => staff.find((s) => s.id === u.staff_id);

// Users without a staff record still have a job; it is shown beside the role.
const EXTRA_TITLES = { 13: "External Auditor (KPMG)", 14: "Support Analyst", 15: "Implementation Consultant", 16: "Account Executive" };

// The API's user statuses: active, pending (invited) and suspended.
const API_STATUS = { invited: "pending", deactivated: "suspended" };

export const people = users.map((u) => ({
  id: u.id,
  name: u.name,
  email: u.email,
  status: API_STATUS[u.status] ?? u.status,
  job_title: staffOf(u)?.role ?? EXTRA_TITLES[u.id] ?? null,
}));

const personById = (id) => people.find((p) => String(p.id) === String(id));

// user id → Set of role ids, from the sample users' roles.
const holdings = new Map(users.map((u) => [u.id, new Set(u.roles.map((label) => roleByLabel(label)?.id).filter(Boolean))]));

const rolesOf = (userId) => [...(holdings.get(userId) ?? [])].map(roleById).filter(Boolean);
const holdersOf = (roleId) => people.filter((p) => holdings.get(p.id)?.has(roleId));

/* ------------------------------------------------------------------ */
/* Presenting a role (RoleResource)                                       */
/* ------------------------------------------------------------------ */

const deletionImpact = (role) => {
  const count = holdersOf(role.id).length;
  if (!count) return null;
  const rights = held.get(role.id)?.size ?? 0;
  return `${count} ${count === 1 ? "person holds" : "people hold"} ${role.label}`
    + (rights ? `, which carries ${rights} ${rights === 1 ? "right" : "rights"}` : "")
    + ". They will lose that access unless another role gives it to them.";
};

const presentRole = (role) => {
  const isProtected = role.name === "admin";
  return {
    id: role.id,
    name: role.name,
    label: role.label,
    description: role.description,
    is_system: role.is_system,
    is_active: role.is_active,
    sort_order: role.sort_order,
    holders: holdersOf(role.id).length,
    permissions: held.get(role.id)?.size ?? 0,
    is_protected: isProtected,
    can_be_deleted: !isProtected,
    deletion_note: isProtected
      ? `${role.label} is the role that administers the system. Removing it would leave nobody able to manage roles, permissions or configuration, so it is the one role that has to stay.`
      : null,
    deletion_impact: deletionImpact(role),
    created_at: role.created_at,
  };
};

/** A ResourceCollection over a paginator, as ->response()->getData(true) gives it. */
const resourcePage = (items, query) => {
  const { data, ...page } = paginate(items, query);
  return {
    data,
    links: { first: page.first_page_url, last: page.last_page_url, prev: page.prev_page_url, next: page.next_page_url },
    meta: { current_page: page.current_page, from: page.from, last_page: page.last_page, links: [], path: page.path, per_page: page.per_page, to: page.to, total: page.total },
  };
};

const slug = (text) => String(text ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/* ------------------------------------------------------------------ */
/* Staged changes and their approval chains                              */
/* ------------------------------------------------------------------ */

const chain = (steps) => steps.map(([step_name, approver_name, action, comments = null], i) => ({ sequence: i + 1, step_name, approver_name, action, comments }));
const currentStep = (c) => c.chain.find((s) => s.action === "pending");
const withWhom = (c) => {
  const step = currentStep(c);
  return step ? `${step.step_name}${step.approver_name ? ` — ${step.approver_name}` : ""}` : null;
};

const STATUS_LABELS = { pending: "Awaiting approval", applied: "Applied", rejected: "Rejected", cancelled: "Cancelled" };

// Permission changes (RolePermissionChange): lines name a permission and grant or revoke it.
const permissionChanges = [
  {
    id: 1, reference: "RPC-2026-0006", role_id: 2, module: "Customers", reason: "Account executives keep asking for customer lists for their territory.",
    lines: [["grant", "customers.export"]], status: "rejected", requested_by: "Brian Otieno", requested_at: daysAgo(48), applied_at: null,
    outcome_note: "Rejected by Mercy Wambui: customer lists leave through Reports, not raw exports.",
    chain: chain([["Head of Sales", "Brian Otieno", "approved"], ["Finance", "Mercy Wambui", "rejected", "Customer lists leave through Reports, not raw exports."]]),
  },
  {
    id: 2, reference: "RPC-2026-0009", role_id: 7, module: "Prospects", reason: "Clean up duplicate prospects after the HR Summit import.",
    lines: [["grant", "prospects.delete"]], status: "cancelled", requested_by: "Dennis Kiprop", requested_at: daysAgo(30), applied_at: null,
    outcome_note: "Called off by Dennis Kiprop: Jane merged the duplicates instead.",
    chain: chain([["Head of Sales", "Brian Otieno", "skipped"], ["CTO/Admin", "Jane Wanjiku", "skipped"]]),
  },
  {
    id: 3, reference: "RPC-2026-0011", role_id: 6, module: "Support", reason: "Agents should reopen tickets a customer replies to after closure.",
    lines: [["grant", "tickets.reopen"]], status: "applied", requested_by: "Faith Achieng", requested_at: daysAgo(14), applied_at: daysAgo(13),
    outcome_note: null,
    chain: chain([["Head of Support", "Grace Njeri", "approved"], ["CTO/Admin", "Jane Wanjiku", "approved"]]),
  },
  {
    id: 4, reference: "RPC-2026-0014", role_id: 3, module: "Reporting", reason: "Sales managers run the Monday pipeline review; scheduling saves an export every week.",
    lines: [["grant", "reports.schedule"]], status: "pending", requested_by: "Jane Wanjiku", requested_at: hoursAgo(20), applied_at: null,
    outcome_note: null,
    chain: chain([["Head of Sales", "Brian Otieno", "approved"], ["CTO/Admin", "Mercy Wambui", "pending"]]),
  },
];

// Role assignment changes (RoleAssignmentChange): lines give or take a role from a person.
const assignmentChanges = [
  {
    id: 1, reference: "RAC-2026-0003", subject_type: "user", subject_id: 9, subject_label: "Dennis Kiprop", reason: "Cover the SME desk while Ann is on leave.",
    lines: [["assign", 9, 3]], status: "cancelled", requested_by: "Brian Otieno", requested_at: daysAgo(60), applied_at: null,
    outcome_note: "Called off by Brian Otieno: Ann's leave was moved.",
    chain: chain([["Head of Sales", "Brian Otieno", "skipped"], ["CTO/Admin", "Jane Wanjiku", "skipped"]]),
  },
  {
    id: 2, reference: "RAC-2026-0005", subject_type: "user", subject_id: 13, subject_label: "Wanjiru Kamau", reason: "External audit for FY2026; read-only access for the engagement.",
    lines: [["assign", 13, 10]], status: "applied", requested_by: "Mercy Wambui", requested_at: daysAgo(12), applied_at: daysAgo(11),
    outcome_note: null,
    chain: chain([["Finance", "Mercy Wambui", "approved"], ["CTO/Admin", "Jane Wanjiku", "approved"]]),
  },
  {
    id: 3, reference: "RAC-2026-0008", subject_type: "user", subject_id: 10, subject_label: "Samuel Kariuki", reason: "Takes on upsell quotations for his renewal accounts.",
    lines: [["assign", 10, 2]], status: "pending", requested_by: "Grace Njeri", requested_at: hoursAgo(5), applied_at: null,
    outcome_note: null,
    chain: chain([["Head of Sales", "Brian Otieno", "pending"], ["CTO/Admin", "Jane Wanjiku", "pending"]]),
  },
];

const summarisePermissionChange = (c) => {
  const grants = c.lines.filter(([a]) => a === "grant").length;
  const revokes = c.lines.length - grants;
  const parts = [grants ? `${grants} ${grants === 1 ? "right" : "rights"} granted` : null, revokes ? `${revokes} taken away` : null].filter(Boolean);
  const said = parts.length ? parts.join(", ") : "no changes";
  return `${said.charAt(0).toUpperCase()}${said.slice(1)}${c.module ? ` in ${c.module}` : ""}`;
};

const presentPermissionChange = (c, full = false) => ({
  id: c.id,
  reference: c.reference,
  role: { id: c.role_id, label: roleById(c.role_id)?.label ?? "Removed role" },
  module: c.module,
  summary: summarisePermissionChange(c),
  reason: c.reason,
  status: c.status,
  status_label: STATUS_LABELS[c.status],
  grants: c.lines.filter(([a]) => a === "grant").length,
  revokes: c.lines.filter(([a]) => a === "revoke").length,
  requested_by: c.requested_by,
  requested_at: c.requested_at,
  applied_at: c.applied_at,
  outcome_note: c.outcome_note,
  with: withWhom(c),
  lines: full
    ? c.lines.map(([action, name]) => {
        const p = permissionByName(name);
        return { action, name, label: p?.label ?? name, group: p?.group ?? null };
      })
    : null,
  chain: full ? c.chain : null,
});

const summariseAssignmentChange = (c) => {
  const given = c.lines.filter(([a]) => a === "assign").length;
  const removed = c.lines.length - given;
  const parts = [given ? `${given} ${given === 1 ? "role" : "roles"} given` : null, removed ? `${removed} taken away` : null].filter(Boolean);
  const said = parts.length ? parts.join(", ") : "no changes";
  return `${said.charAt(0).toUpperCase()}${said.slice(1)}${c.subject_label ? ` for ${c.subject_label}` : ""}`;
};

const presentAssignmentChange = (c, full = false) => ({
  id: c.id,
  reference: c.reference,
  subject: { type: c.subject_type, id: c.subject_id, label: c.subject_label },
  summary: summariseAssignmentChange(c),
  reason: c.reason,
  status: c.status,
  status_label: STATUS_LABELS[c.status],
  assignments: c.lines.filter(([a]) => a === "assign").length,
  removals: c.lines.filter(([a]) => a === "remove").length,
  requested_by: c.requested_by,
  requested_at: c.requested_at,
  applied_at: c.applied_at,
  outcome_note: c.outcome_note,
  with: withWhom(c),
  lines: full
    ? c.lines.map(([action, userId, roleId]) => ({ action, user: personById(userId)?.name ?? "Former user", role: roleById(roleId)?.label ?? "Removed role" }))
    : null,
  chain: full ? c.chain : null,
});

const nextReference = (prefix, list) => `${prefix}-2026-${String(Math.max(0, ...list.map((c) => Number(c.reference.split("-").pop()))) + 1).padStart(4, "0")}`;

// New proposals go to the same two signatures the sample chains use.
const newChain = (first) => chain([[first, first === "Head of Sales" ? "Brian Otieno" : "Grace Njeri", "pending"], ["CTO/Admin", "Jane Wanjiku", "pending"]]);

const callOff = (list, present, id, body) => {
  const change = list.find((c) => String(c.id) === String(id));
  if (!change) return notFound(`Change ${id} was not found.`);
  const reason = String(body.reason ?? "").trim();
  if (!reason) return invalid({ reason: ["Say why this is being called off."] });
  if (reason.length < 5) return invalid({ reason: ["Give a reason somebody else could understand later."] });
  if (change.status !== "pending") return conflict(`${change.reference} is no longer awaiting a decision, so there is nothing to call off.`);

  change.status = "cancelled";
  change.outcome_note = `Called off by Jane Wanjiku: ${reason}`;
  change.chain.forEach((s) => s.action === "pending" && (s.action = "skipped"));
  return ok(present(change), `${change.reference} was called off`);
};

/* ------------------------------------------------------------------ */
/* Permission diffs                                                       */
/* ------------------------------------------------------------------ */

const describePermission = (p) => ({ id: p.id, name: p.name, label: p.label, group: p.group });

const permissionDiff = (roleId, module, ids) => {
  const wanted = new Set(ids.map(String));
  const scope = permissions.filter((p) => !module || p.module === module);
  const current = held.get(roleId) ?? new Set();
  return {
    grant: scope.filter((p) => wanted.has(String(p.id)) && !current.has(p.id)),
    revoke: scope.filter((p) => !wanted.has(String(p.id)) && current.has(p.id)),
  };
};

const pendingPermissionLines = (roleId) => {
  const out = new Map();
  permissionChanges
    .filter((c) => c.status === "pending" && c.role_id === roleId)
    .forEach((c) => c.lines.forEach(([action, name]) => {
      const p = permissionByName(name);
      if (p) out.set(p.id, { action, reference: c.reference });
    }));
  return out;
};

const groupBy = (items, key) => {
  const groups = new Map();
  items.forEach((item) => {
    if (!groups.has(item[key])) groups.set(item[key], []);
    groups.get(item[key]).push(item);
  });
  return [...groups.entries()];
};

/* ------------------------------------------------------------------ */
/* Role assignment diffs                                                  */
/* ------------------------------------------------------------------ */

const pendingAssignmentLines = (userId) => {
  const out = new Map();
  assignmentChanges
    .filter((c) => c.status === "pending")
    .forEach((c) => c.lines.forEach(([action, uid, roleId]) => uid === userId && out.set(roleId, { action, reference: c.reference })));
  return out;
};

const assignmentDiffForUser = (userId, roleIds) => {
  const wanted = new Set(roleIds.map(String));
  const current = holdings.get(userId) ?? new Set();
  const active = roles.filter((r) => r.is_active);
  return {
    assign: active.filter((r) => wanted.has(String(r.id)) && !current.has(r.id)),
    remove: roles.filter((r) => !wanted.has(String(r.id)) && current.has(r.id)),
  };
};

const assignmentDiffForRole = (roleId, userIds) => {
  const wanted = new Set(userIds.map(String));
  return {
    assign: people.filter((p) => wanted.has(String(p.id)) && !holdings.get(p.id)?.has(roleId)),
    remove: people.filter((p) => !wanted.has(String(p.id)) && holdings.get(p.id)?.has(roleId)),
  };
};

const outcomeMessage = (c, summary, tail) => {
  const step = currentStep(c);
  return `${c.reference}: ${summary}. `
    + (step ? `It is with ${step.step_name}${step.approver_name ? ` (${step.approver_name})` : ""}.` : "It is awaiting approval.")
    + ` ${tail}`;
};

/** One person, as the assignment screen shows them: job beside role. */
const presentPerson = (p) => ({
  id: p.id,
  name: p.name,
  email: p.email,
  job_title: p.job_title,
  status: p.status,
});

/* ------------------------------------------------------------------ */
/* Routes                                                                 */
/* ------------------------------------------------------------------ */

export default [
  // ---- Roles ----
  ["get", "tenant/roles-choices", () =>
    ok(roles.filter((r) => r.is_active).sort((a, b) => a.sort_order - b.sort_order).map(({ id, name, label }) => ({ id, name, label })))],

  ["get", "tenant/roles-summary", () =>
    ok({
      roles: roles.length,
      active: roles.filter((r) => r.is_active).length,
      system: roles.filter((r) => r.is_system).length,
      permissions: permissions.length,
      unheld: roles.filter((r) => !holdersOf(r.id).length).length,
      without_rights: roles.filter((r) => !(held.get(r.id)?.size)).length,
    })],

  ["get", "tenant/roles", ({ query }) => {
    const list = roles
      .filter((r) => matches(query.search, r.label, r.name, r.description))
      .filter((r) => query.is_active === undefined || query.is_active === "" || r.is_active === (String(query.is_active) === "1"))
      .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label))
      .map(presentRole);
    return ok(resourcePage(list, query));
  }],

  ["post", "tenant/roles", ({ body }) => {
    const label = String(body.label ?? "").trim();
    const name = slug(body.name || label);
    if (!label) return invalid({ label: ["The name field is required."] });
    if (roles.some((r) => r.name === name)) return invalid({ name: ["The internal key has already been taken."] });

    const role = { id: nextId(roles), name, label, description: body.description || null, is_system: false, is_active: body.is_active ?? true, sort_order: Number(body.sort_order) || 0, created_at: now() };
    roles.push(role);
    held.set(role.id, new Set());
    return created(presentRole(role), `${label} was created. It grants nothing until permissions are assigned to it.`);
  }],

  ["get", "tenant/roles/:id/holders", ({ params, query }) => {
    const role = roleById(params.id);
    if (!role) return notFound(`Role ${params.id} was not found.`);
    const list = holdersOf(role.id).sort((a, b) => a.name.localeCompare(b.name)).map(presentPerson);
    return ok(paginate(list, query));
  }],

  ["get", "tenant/roles/:id", ({ params }) => {
    const role = roleById(params.id);
    return role ? ok(presentRole(role)) : notFound(`Role ${params.id} was not found.`);
  }],

  ["put", "tenant/roles/:id", ({ params, body }) => {
    const role = roleById(params.id);
    if (!role) return notFound(`Role ${params.id} was not found.`);

    const switchingOff = body.is_active === false && role.is_active;
    if (role.name === "admin" && switchingOff) return conflict(`${role.label} administers the system and cannot be switched off.`);
    const impact = deletionImpact(role);
    if (switchingOff && !body.confirm && impact) {
      return conflict(`${impact} Confirm to switch it off anyway.`, { requires_confirmation: true, holders: holdersOf(role.id).length });
    }

    if (body.label !== undefined) role.label = String(body.label).trim() || role.label;
    if (body.name !== undefined && role.name !== "admin") role.name = slug(body.name) || role.name;
    if (body.description !== undefined) role.description = body.description || null;
    if (body.sort_order !== undefined) role.sort_order = Number(body.sort_order) || 0;
    if (body.is_active !== undefined) role.is_active = !!body.is_active;

    return ok(presentRole(role), `${role.label} was updated${switchingOff ? ". Anybody holding it has lost the access it carried" : ""}`);
  }],

  ["delete", "tenant/roles/:id", ({ params, query }) => {
    const role = roleById(params.id);
    if (!role) return notFound(`Role ${params.id} was not found.`);
    if (role.name === "admin") return conflict(presentRole(role).deletion_note);

    const impact = deletionImpact(role);
    const count = holdersOf(role.id).length;
    if (impact && !query.confirm) return conflict(`${impact} Confirm to remove it anyway.`, { requires_confirmation: true, holders: count });

    roles.splice(roles.indexOf(role), 1);
    held.delete(role.id);
    holdings.forEach((set) => set.delete(role.id));
    return ok(null, `${role.label} was removed${count ? `, and ${count} ${count === 1 ? "person" : "people"} lost the access it carried` : ""}`);
  }],

  // ---- What a role may do ----
  ["get", "tenant/role-permissions/modules", () =>
    ok({
      modules: groupBy(permissions, "module")
        .map(([module, items]) => ({ module, rights: items.length, groups: [...new Set(items.map((p) => p.group))] }))
        .sort((a, b) => a.module.localeCompare(b.module)),
      unfiled: 0,
    })],

  ["get", "tenant/role-permissions/changes/:id", ({ params }) => {
    const change = permissionChanges.find((c) => String(c.id) === String(params.id));
    return change ? ok(presentPermissionChange(change, true)) : notFound(`Permission change ${params.id} was not found.`);
  }],

  ["get", "tenant/role-permissions/changes", ({ query }) => {
    const list = permissionChanges
      .filter((c) => !query.status || c.status === query.status)
      .sort((a, b) => b.requested_at.localeCompare(a.requested_at))
      .map((c) => presentPermissionChange(c));
    return ok(paginate(list, query));
  }],

  ["post", "tenant/role-permissions/changes/:id/cancel", ({ params, body }) =>
    callOff(permissionChanges, presentPermissionChange, params.id, body)],

  ["get", "tenant/role-permissions/:roleId/module", ({ params, query }) => {
    const role = roleById(params.roleId);
    if (!role) return notFound(`Role ${params.roleId} was not found.`);
    if (!query.module) return invalid({ module: ["The module field is required."] });

    const current = held.get(role.id);
    const pending = pendingPermissionLines(role.id);
    const items = permissions
      .filter((p) => p.module === query.module)
      .map((p) => ({ id: p.id, name: p.name, label: p.label, group: p.group, held: current.has(p.id), pending: pending.get(p.id) ?? null }));

    return ok({ module: query.module, groups: groupBy(items, "group").map(([group, list]) => ({ group, permissions: list })) });
  }],

  ["post", "tenant/role-permissions/:roleId/preview", ({ params, body }) => {
    const role = roleById(params.roleId);
    if (!role) return notFound(`Role ${params.roleId} was not found.`);
    const diff = permissionDiff(role.id, body.module, body.permission_ids ?? []);
    return ok({ grant: diff.grant.map(describePermission), revoke: diff.revoke.map(describePermission), has_changes: !!(diff.grant.length || diff.revoke.length) });
  }],

  ["post", "tenant/role-permissions/:roleId/propose", ({ params, body }) => {
    const role = roleById(params.roleId);
    if (!role) return notFound(`Role ${params.roleId} was not found.`);
    const diff = permissionDiff(role.id, body.module, body.permission_ids ?? []);
    if (!diff.grant.length && !diff.revoke.length) return conflict("Nothing would change, so there is nothing to send.");

    const change = {
      id: nextId(permissionChanges), reference: nextReference("RPC", permissionChanges), role_id: role.id, module: body.module || null, reason: body.reason || null,
      lines: [...diff.grant.map((p) => ["grant", p.name]), ...diff.revoke.map((p) => ["revoke", p.name])],
      status: "pending", requested_by: "Jane Wanjiku", requested_at: now(), applied_at: null, outcome_note: null,
      chain: newChain("Head of Sales"),
    };
    permissionChanges.push(change);

    const summary = `${summarisePermissionChange(change)} for ${role.label}`;
    return created(presentPermissionChange(change), outcomeMessage(change, summary, `Nothing changes for ${role.label} until it is approved.`));
  }],

  ["get", "tenant/role-permissions/:roleId", ({ params }) => {
    const role = roleById(params.roleId);
    if (!role) return notFound(`Role ${params.roleId} was not found.`);

    const mine = permissions.filter((p) => held.get(role.id)?.has(p.id));
    return ok({
      role: { id: role.id, name: role.name, label: role.label, description: role.description, is_system: role.is_system, is_editable: !(role.is_system && role.name === "admin") },
      total: mine.length,
      modules: groupBy([...mine].sort((a, b) => a.module.localeCompare(b.module) || a.group.localeCompare(b.group)), "module")
        .map(([module, list]) => ({ module, permissions: list.map(describePermission) })),
    });
  }],

  // ---- Who holds which role ----
  ["get", "tenant/role-assignments-summary", () => {
    const counted = people.filter((p) => p.status !== "suspended");
    const n = (p) => holdings.get(p.id)?.size ?? 0;
    return ok({
      people: counted.length,
      with_roles: counted.filter((p) => n(p) > 0).length,
      without_roles: counted.filter((p) => n(p) === 0).length,
      multiple_roles: counted.filter((p) => n(p) > 1).length,
      pending_changes: assignmentChanges.filter((c) => c.status === "pending").length,
    });
  }],

  ["get", "tenant/role-assignments/changes/:id", ({ params }) => {
    const change = assignmentChanges.find((c) => String(c.id) === String(params.id));
    return change ? ok(presentAssignmentChange(change, true)) : notFound(`Role change ${params.id} was not found.`);
  }],

  ["get", "tenant/role-assignments/changes", ({ query }) => {
    const list = assignmentChanges
      .filter((c) => !query.status || c.status === query.status)
      .filter((c) => !query.subject_id || String(c.subject_id) === String(query.subject_id))
      .sort((a, b) => b.requested_at.localeCompare(a.requested_at))
      .map((c) => presentAssignmentChange(c));
    return ok(paginate(list, query));
  }],

  ["post", "tenant/role-assignments/changes/:id/cancel", ({ params, body }) =>
    callOff(assignmentChanges, presentAssignmentChange, params.id, body)],

  ["get", "tenant/role-assignments/user/:id", ({ params }) => {
    const person = personById(params.id);
    if (!person) return notFound(`User ${params.id} was not found.`);

    const pending = pendingAssignmentLines(person.id);
    const mine = holdings.get(person.id) ?? new Set();
    return ok({
      user: presentPerson(person),
      roles: roles
        .filter((r) => r.is_active)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((r) => ({ id: r.id, name: r.name, label: r.label, description: r.description, rights: held.get(r.id)?.size ?? 0, held: mine.has(r.id), pending: pending.get(r.id) ?? null })),
    });
  }],

  ["post", "tenant/role-assignments/user/:id/preview", ({ params, body }) => {
    const person = personById(params.id);
    if (!person) return notFound(`User ${params.id} was not found.`);
    const diff = assignmentDiffForUser(person.id, body.role_ids ?? []);
    const describe = (r) => ({ id: r.id, label: r.label });
    return ok({ assign: diff.assign.map(describe), remove: diff.remove.map(describe), has_changes: !!(diff.assign.length || diff.remove.length) });
  }],

  ["post", "tenant/role-assignments/user/:id/propose", ({ params, body }) => {
    const person = personById(params.id);
    if (!person) return notFound(`User ${params.id} was not found.`);
    const diff = assignmentDiffForUser(person.id, body.role_ids ?? []);
    if (!diff.assign.length && !diff.remove.length) return conflict("Nothing would change, so there is nothing to send.");

    const change = {
      id: nextId(assignmentChanges), reference: nextReference("RAC", assignmentChanges), subject_type: "user", subject_id: person.id, subject_label: person.name, reason: body.reason || null,
      lines: [...diff.assign.map((r) => ["assign", person.id, r.id]), ...diff.remove.map((r) => ["remove", person.id, r.id])],
      status: "pending", requested_by: "Jane Wanjiku", requested_at: now(), applied_at: null, outcome_note: null,
      chain: newChain("Head of Sales"),
    };
    assignmentChanges.push(change);
    return created(presentAssignmentChange(change), outcomeMessage(change, summariseAssignmentChange(change), "Nobody gains or loses access until it is approved."));
  }],

  ["post", "tenant/role-assignments/role/:roleId/preview", ({ params, body }) => {
    const role = roleById(params.roleId);
    if (!role) return notFound(`Role ${params.roleId} was not found.`);
    const diff = assignmentDiffForRole(role.id, body.user_ids ?? []);
    const describe = (p) => ({ id: p.id, label: p.name });
    return ok({ assign: diff.assign.map(describe), remove: diff.remove.map(describe), has_changes: !!(diff.assign.length || diff.remove.length) });
  }],

  ["post", "tenant/role-assignments/role/:roleId/propose", ({ params, body }) => {
    const role = roleById(params.roleId);
    if (!role) return notFound(`Role ${params.roleId} was not found.`);
    const diff = assignmentDiffForRole(role.id, body.user_ids ?? []);
    if (!diff.assign.length && !diff.remove.length) return conflict("Nothing would change, so there is nothing to send.");

    const change = {
      id: nextId(assignmentChanges), reference: nextReference("RAC", assignmentChanges), subject_type: "role", subject_id: role.id, subject_label: role.label, reason: body.reason || null,
      lines: [...diff.assign.map((p) => ["assign", p.id, role.id]), ...diff.remove.map((p) => ["remove", p.id, role.id])],
      status: "pending", requested_by: "Jane Wanjiku", requested_at: now(), applied_at: null, outcome_note: null,
      chain: newChain("Head of Sales"),
    };
    assignmentChanges.push(change);
    return created(presentAssignmentChange(change), outcomeMessage(change, summariseAssignmentChange(change), "Nobody gains or loses access until it is approved."));
  }],

  ["get", "tenant/role-assignments", ({ query }) => {
    const list = people
      .filter((p) => matches(query.search, p.name, p.email))
      .filter((p) => !query.status || p.status === query.status)
      .filter((p) => !query.role_id || holdings.get(p.id)?.has(Number(query.role_id)))
      .filter((p) => !(String(query.without_role) === "1" || query.without_role === true) || !holdings.get(p.id)?.size)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => {
        const mine = rolesOf(p.id);
        return { ...presentPerson(p), roles: mine.map((r) => ({ id: r.id, label: r.label })), has_no_role: mine.length === 0 };
      });
    return ok(paginate(list, query));
  }],
];
